import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, Users, Filter, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReportLayout from '../components/ReportLayout';

const escapeCsv = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toCsv = (rows, headers) => {
  const headerLine = headers.map((h) => escapeCsv(h.label)).join(',');
  const dataLines = rows.map((r) => headers.map((h) => escapeCsv(h.value(r))).join(','));
  return [headerLine, ...dataLines].join('\n');
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatLastVisit = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const ReportsClients = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  const [filter, setFilter] = useState('all'); // all | active | with_conditions
  const [searchTerm, setSearchTerm] = useState('');

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await api.get('/clients');
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load client report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return clients
      .filter((c) => {
        if (filter === 'active') return !!c.is_active;
        if (filter === 'with_conditions') return (c.medical_conditions || []).length > 0;
        return true;
      })
      .filter((c) => {
        if (!q) return true;
        return (
          String(c.first_name || '').toLowerCase().includes(q) ||
          String(c.last_name || '').toLowerCase().includes(q) ||
          String(c.phone || '').toLowerCase().includes(q) ||
          String(c.email || '').toLowerCase().includes(q) ||
          String(c.id || '').toLowerCase().includes(q)
        );
      });
  }, [clients, filter, searchTerm]);

  const exportCsv = () => {
    const headers = [
      { label: 'Client ID', value: (r) => r.id },
      { label: 'First Name', value: (r) => r.first_name },
      { label: 'Last Name', value: (r) => r.last_name },
      { label: 'Phone', value: (r) => r.phone },
      { label: 'Email', value: (r) => r.email },
      { label: 'Gender', value: (r) => r.gender },
      { label: 'Medical Conditions', value: (r) => (r.medical_conditions || []).join('|') },
      { label: 'Nutrition Goals', value: (r) => (r.nutrition_goals || []).join('|') },
      { label: 'Last Updated', value: (r) => r.updated_at },
      { label: 'Is Active', value: (r) => r.is_active },
    ];

    const csv = toCsv(filtered, headers);
    downloadCsv(`client_report_${filter}_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  // Left slot - filter controls
  const leftSlot = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Clients</option>
              <option value="active">Active Clients</option>
              <option value="with_conditions">With Medical Conditions</option>
            </select>
          </div>
        </div>

        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, phone, email, ID..."
            className="w-full lg:w-80 pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
    </div>
  );

  // Right slot - summary
  const rightSlot = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-2">
        <Users size={18} className="text-green-600" />
        <p className="font-semibold text-gray-800">Client Summary</p>
      </div>
      <p className="text-sm text-gray-500 mt-2">{filtered.length} clients found</p>
    </div>
  );

  // Tables slot - client list
  const tablesSlot = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-green-600" />
          <p className="font-semibold text-gray-800">Client List</p>
        </div>
        <p className="text-sm text-gray-500">{filtered.length} rows</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-6 py-16 text-center text-gray-500">No clients found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full report-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Conditions</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nutrition Goals</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Last Updated</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${c.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'} border`}>
                        <span className="text-sm font-bold">
                          {(c.first_name?.[0] || '') + (c.last_name?.[0] || '')}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {c.first_name} {c.last_name}
                        </p>
                        <p className="text-xs text-gray-400 font-mono">#{String(c.id).slice(0, 8)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="space-y-1">
                      <div>{c.phone || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{c.email || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {(c.medical_conditions || []).length ? (c.medical_conditions || []).slice(0, 2).join(', ') : 'None'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {(c.nutrition_goals || []).length ? (c.nutrition_goals || []).slice(0, 1)[0].replace(/_/g, ' ') : 'No program'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{c.updated_at ? formatLastVisit(c.updated_at) : 'No visits'}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => navigate(`/clients/${c.id}`)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View client"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

return (
    <ReportLayout
      title="Client Report"
      description="Audit and track client registry changes"
      onExportCsv={exportCsv}
      onPrint={() => window.print()}
      printLink="/reports/clients/print"
      kpis={[]}
      leftSlot={leftSlot}
      rightSlot={rightSlot}
      tablesSlot={tablesSlot}
    />
  );
};

export default ReportsClients;

